import { CircularProgress, Skeleton, Typography } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import { styled } from '@mui/material/styles';
import { useCallback, useRef, useState } from 'react';

const PreviewContainer = styled('div')({
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const SkeletonScrollContainer = styled('div')({
  overflowY: 'auto',
});

const SkeletonDocument = styled(Column)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(8),
  width: '100%',
  aspectRatio: '1 / 1.414',
  flexShrink: 0,
  overflow: 'hidden',
}));

const SkeletonHeader = styled(Row)({
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

const SkeletonLogoAndLines = styled(Row)({
  alignItems: 'flex-start',
});

const SkeletonSpinnerArea = styled(Column)({
  flex: 1,
});

const SECOND_COLUMN_MIN_WIDTH = 380;

const DocumentSkeleton: React.FC<{ showSpinner?: boolean }> = ({
  showSpinner,
}) => {
  const [scrollWidth, setScrollWidth] = useState<number | undefined>();
  const scrollObserverRef = useRef<ResizeObserver | null>(null);

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollObserverRef.current?.disconnect();
    scrollObserverRef.current = null;
    if (node) {
      setScrollWidth(node.clientWidth);
      const observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;
        if (width) setScrollWidth(Math.floor(width));
      });
      observer.observe(node);
      scrollObserverRef.current = observer;
    }
  }, []);

  const showSecondColumn =
    scrollWidth === undefined || scrollWidth >= SECOND_COLUMN_MIN_WIDTH;

  return (
    <PreviewContainer>
      <SkeletonScrollContainer ref={scrollRef}>
        <SkeletonDocument spacing="md">
          <SkeletonHeader spacing="md">
            <SkeletonLogoAndLines spacing="sm">
              <Skeleton
                variant="rectangular"
                width={52}
                height={52}
                style={{ borderRadius: 4, flexShrink: 0 }}
              />
              <Column>
                <Skeleton
                  variant="text"
                  width={120}
                  style={{ fontSize: '1rem' }}
                />
                <Skeleton variant="text" width={160} />
                <Skeleton variant="text" width={100} />
              </Column>
            </SkeletonLogoAndLines>
            {showSecondColumn && (
              <Column align="flex-end">
                <Skeleton
                  variant="text"
                  width={110}
                  style={{ fontSize: '1rem' }}
                />
                <Skeleton variant="text" width={130} />
                <Skeleton variant="text" width={90} />
              </Column>
            )}
          </SkeletonHeader>
          {showSpinner && (
            <SkeletonSpinnerArea centered spacing="sm" justify="center">
              <CircularProgress size={24} />
              <Typography variant="body2">File uploading...</Typography>
            </SkeletonSpinnerArea>
          )}
        </SkeletonDocument>
      </SkeletonScrollContainer>
    </PreviewContainer>
  );
};

export default DocumentSkeleton;
